/*!
 * Copyright (c) REWE Digital GmbH
 */
import { h, Build, Host } from "@stencil/core";
import Splide from "@splidejs/splide";
import { defaultOptions } from "./defaults";
import splideCss from "@splidejs/splide/dist/css/splide.min.css";
/**
 * The vx-image-gallery displays two carousels - the main carousel and the thumbnail carousel.
 * When the main carousel moves, the active thumbnail will change. Also, thumbnails are
 * clickable to navigate the main carousel.
 * This component is based on the Splide-library (https://splidejs.com).
 */
// eslint-disable-next-line require-jsdoc
export class VxContentSlider {
    constructor() {
        /**
         * Merges the default with passed options.
         */
        this.initOptions = ({ speed, direction } = this) => {
            this.options = Object.assign(Object.assign(Object.assign({}, defaultOptions), this.options), { speed, direction });
        };
        /**
         * Mounts the main slider.
         */
        this.mountSplide = () => {
            var _a;
            if (Build.isServer)
                return;
            (_a = this.splide) === null || _a === void 0 ? void 0 : _a.destroy();
            this.splide = new Splide(this.slider, this.options).mount();
            this.sliderReady.emit(this.splide);
        };
        this.options = undefined;
        this.direction = 'ltr';
        this.speed = 1200;
    }
    /**
     * @inheritDoc
     */
    componentWillLoad() {
        this.initOptions();
    }
    /**
     * @inheritDoc
     */
    componentDidLoad() {
        this.mountSplide();
    }
    /**
     * @inheritdoc
     */
    render() {
        return (h(Host, { key: 'fd19bf4f84825f62a697bb39d6bf615eb11efaf1' }, h("div", { key: '5345b5c9a0a674d93bde467cc49a1d03ffde1ede' }, h("section", { key: '53bf30ad34e2cfab039b2bf082b1dfabbce15e68', ref: el => this.slider = el, id: "main-slider", class: "splide", "aria-label": this.options.label }, h("style", { key: 'f4d36e74c35a0faa424825e3b7528f9dd68e5ba3', innerHTML: splideCss }), h("div", { key: 'f3f0dad92b6a12bb274f9fea1ee7b658ef3f6077', class: "splide__track" }, h("div", { key: '169419928116be77dfe04c257175544cdaac6b20', class: "splide__list" }, h("slot", { key: '4a2f631899c61e93df48cb34df1643ae46821bc4' })))))));
    }
    static get is() { return "vx-content-slider"; }
    static get encapsulation() { return "scoped"; }
    static get originalStyleUrls() {
        return {
            "$": ["vx-content-slider.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["vx-content-slider.css"]
        };
    }
    static get properties() {
        return {
            "options": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "Partial<CarouselOptions>",
                    "resolved": "{ [x: string]: any; labelNextSlideButton?: string; labelPrevSlideButton?: string; imageQuality?: number; imageFit?: string; type?: string; role?: string; waitForTransition?: boolean; autoWidth?: boolean; autoHeight?: boolean; start?: number; arrowPath?: string; autoplay?: boolean | \"pause\"; interval?: number; pauseOnHover?: boolean; pauseOnFocus?: boolean; resetProgress?: boolean; lazyLoad?: boolean | \"nearby\" | \"sequential\"; preloadPages?: number; keyboard?: boolean | \"global\" | \"focused\"; wheel?: boolean; wheelMinThreshold?: number; wheelSleep?: number; releaseWheel?: boolean; direction?: \"ltr\" | \"rtl\" | \"ttb\"; cover?: boolean; slideFocus?: boolean; isNavigation?: boolean; trimSpace?: boolean | \"move\"; omitEnd?: boolean; updateOnMove?: boolean; mediaQuery?: \"min\" | \"max\"; focusableNodes?: string; noDrag?: string; live?: boolean; useScroll?: boolean; breakpoints?: Record<string | number, ResponsiveOptions>; reducedMotion?: Options; classes?: Record<string, string>; i18n?: Record<string, string>; label?: string; labelledby?: string; speed?: number; rewind?: boolean; rewindSpeed?: number; rewindByDrag?: boolean; width?: string | number; height?: string | number; fixedWidth?: string | number; fixedHeight?: string | number; heightRatio?: number; perPage?: number; perMove?: number; clones?: number; cloneStatus?: boolean; focus?: number | \"center\"; gap?: string | number; padding?: string | number | { left?: string | number; right?: string | number; } | { top?: string | number; bottom?: string | number; }; arrows?: boolean; pagination?: boolean; paginationKeyboard?: boolean; paginationDirection?: \"ltr\" | \"rtl\" | \"ttb\"; easing?: string; easingFunc?: (t: number) => number; drag?: boolean | \"free\"; snap?: boolean; dragMinThreshold?: number | { mouse: number; touch: number; }; flickPower?: number; flickMaxPages?: number; destroy?: boolean | \"completely\"; }",
                    "references": {
                        "Partial": {
                            "location": "global",
                            "id": "global::Partial"
                        },
                        "CarouselOptions": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/vx-content-slider/types/index.ts::CarouselOptions"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "see",
                            "text": "https://splidejs.com/guides/options/#options"
                        }],
                    "text": "The options for the main carousel."
                }
            },
            "direction": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "'ltr' | 'rtl' | 'ttb'",
                    "resolved": "\"ltr\" | \"rtl\" | \"ttb\"",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "see",
                            "text": "https://splidejs.com/guides/options/#direction"
                        }, {
                            "name": "default",
                            "text": "'ttb'"
                        }],
                    "text": "The direction of the gallery."
                },
                "attribute": "direction",
                "reflect": false,
                "defaultValue": "'ltr'"
            },
            "speed": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The duration of the slide-animation."
                },
                "attribute": "speed",
                "reflect": false,
                "defaultValue": "1200"
            }
        };
    }
    static get events() {
        return [{
                "method": "sliderReady",
                "name": "sliderReady",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "Splide",
                    "resolved": "Splide",
                    "references": {
                        "Splide": {
                            "location": "global",
                            "id": "global::Splide"
                        }
                    }
                }
            }];
    }
}
//# sourceMappingURL=vx-content-slider.js.map
